import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import { api, session, setUnauthorizedHandler, type Language, type Teacher } from "./api";

export type ThemeMode = "system" | "light" | "dark";
export type Crumb = { label: string; to?: string };
const THEME_KEY = "jange.studio.theme";
const RECENT_KEY = "jange.studio.recent";
export type Recent = { id: string; title: string; at: number };
const readRecent = (): Recent[] => { try { return JSON.parse(localStorage.getItem(RECENT_KEY) ?? "[]") as Recent[]; } catch { return []; } };

type Ctx = {
  teacher: Teacher | null;
  signIn: (token: string, t: Teacher) => void;
  signOut: () => void;
  countryId: string;
  setCountryId: (id: string) => void;
  languages: Language[];
  toast: (msg: string, kind?: "ok" | "err") => void;
  theme: ThemeMode;
  setTheme: (m: ThemeMode) => void;
  crumbs: Crumb[];
  setCrumbs: (c: Crumb[]) => void;
  paletteOpen: boolean;
  setPaletteOpen: (o: boolean) => void;
  recent: Recent[];
  pushRecent: (r: Omit<Recent, "at">) => void;
};
const StudioCtx = createContext<Ctx | null>(null);
const COUNTRY_KEY = "jange.studio.country";
const DEFAULT_COUNTRY = (import.meta.env.VITE_DEFAULT_COUNTRY as string | undefined) ?? "SN";

export function StudioProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [teacher, setTeacher] = useState<Teacher | null>(() => (session.token() ? session.teacher() : null));
  const [countryId, setCountry] = useState(() => { try { return localStorage.getItem(COUNTRY_KEY) ?? DEFAULT_COUNTRY; } catch { return DEFAULT_COUNTRY; } });
  const [toastState, setToast] = useState<{ msg: string; kind: "ok" | "err"; n: number } | null>(null);

  const [theme, setThemeState] = useState<ThemeMode>(() => { try { return (localStorage.getItem(THEME_KEY) as ThemeMode | null) ?? "system"; } catch { return "system"; } });
  const [crumbs, setCrumbs] = useState<Crumb[]>([]);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [recent, setRecent] = useState<Recent[]>(readRecent);
  const pushRecent = useCallback((r: Omit<Recent, "at">) => setRecent((xs) => {
    const next = [{ ...r, at: Date.now() }, ...xs.filter((x) => x.id !== r.id)].slice(0, 6);
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* rien */ }
    return next;
  }), []);

  // Thème : « système » suit le réglage du poste, y compris quand il change.
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const apply = () => { document.documentElement.dataset.theme = theme === "system" ? (mq.matches ? "dark" : "light") : theme; };
    apply();
    if (theme !== "system") return;
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, [theme]);

  const signOut = useCallback(() => { session.clear(); setTeacher(null); qc.clear(); }, [qc]);
  useEffect(() => { setUnauthorizedHandler(() => { setTeacher(null); qc.clear(); }); }, [qc]);

  // Rôles à jour au chargement (un éditeur a pu les changer depuis la dernière connexion).
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, enabled: !!teacher, staleTime: 5 * 60_000 });
  useEffect(() => { if (me.data && session.token()) { session.save(session.token()!, me.data); setTeacher(me.data); } }, [me.data]);

  const langs = useQuery({ queryKey: ["languages", countryId], queryFn: () => api.countryLanguages(countryId), enabled: !!teacher, staleTime: 30 * 60_000 });

  useEffect(() => {
    if (!toastState) return;
    const t = setTimeout(() => setToast(null), toastState.kind === "err" ? 6000 : 3000);
    return () => clearTimeout(t);
  }, [toastState]);

  const value = useMemo<Ctx>(() => ({
    teacher,
    signIn: (token, t) => { session.save(token, t); setTeacher(t); },
    signOut,
    countryId,
    setCountryId: (id) => { try { localStorage.setItem(COUNTRY_KEY, id); } catch { /* rien */ } setCountry(id); },
    languages: langs.data ?? [{ code: "fr", name: "Français", nativeName: null, isInstruction: true }],
    toast: (msg, kind = "ok") => setToast({ msg, kind, n: Date.now() }),
    theme,
    setTheme: (m) => { try { localStorage.setItem(THEME_KEY, m); } catch { /* rien */ } setThemeState(m); },
    crumbs, setCrumbs, paletteOpen, setPaletteOpen, recent, pushRecent,
  }), [teacher, signOut, countryId, langs.data, theme, crumbs, paletteOpen, recent, pushRecent]);

  return (
    <StudioCtx.Provider value={value}>
      {children}
      {toastState && <div key={toastState.n} role="status" className={`toast ${toastState.kind === "err" ? "err" : ""}`}>{toastState.kind === "err" ? <AlertTriangle size={18} /> : <CheckCircle2 size={18} />}<span>{toastState.msg}</span></div>}
    </StudioCtx.Provider>
  );
}

/** Fil d'Ariane de la barre du haut, fourni par la page affichée. */
export function useCrumbs(c: Crumb[]) {
  const { setCrumbs } = useStudio();
  const key = JSON.stringify(c);
  useEffect(() => { setCrumbs(JSON.parse(key) as Crumb[]); return () => setCrumbs([]); }, [key, setCrumbs]);
}

export function useStudio() {
  const c = useContext(StudioCtx);
  if (!c) throw new Error("useStudio hors de StudioProvider");
  return c;
}
